// @file Composable to add double click to post functionality to surface
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { onMounted, onUnmounted } from 'vue'

type MouseEventHandler = (e: MouseEvent) => void
type HTMLElementWithMouseHandlers = HTMLElement & {
  $_onDblClick?: MouseEventHandler
}

function checkIfClickAtEmptySpaceOfSection(clientX: number, clientY: number): number | undefined {
  const elementAtPoint = document.elementFromPoint(clientX, clientY)
  if (elementAtPoint instanceof HTMLElement) {
    const sectionElement = elementAtPoint.closest<HTMLElement>('section[data-id], section[id^="section-"]')
    if (sectionElement != null) {
      const dataId = sectionElement.dataset.id
      if (dataId != null) {
        const parsedId = Number(dataId)
        if (!isNaN(parsedId)) {
          return parsedId
        }
      } else {
        const idMatch = sectionElement.id.match(/^section-(\d+)$/)
        if (idMatch != null) {
          return Number(idMatch[1])
        }
      }
    }
  }

  return undefined
}

export function useDoubleClickToPost(wallContainerEl?: HTMLElement, wishListEl?: HTMLElement): void {
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceStore = useSurfaceStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()

  onMounted(() => {
    const defaultWallContainerEl = document.querySelector<HTMLElement>('#wall-container')
    const wallContainerElement = wallContainerEl ?? defaultWallContainerEl

    let wishListElement = wishListEl ?? document.querySelector<HTMLElement>('#wish-list')

    let timelineWishListEl = document.querySelector<HTMLElement>('.wish-list-content')
    let padletSectionsEl = document.querySelector<HTMLElement>('padlet-sections')

    const handleDoubleClick: MouseEventHandler = (e) => {
      if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop) return
      if (!surfacePermissionsStore.canIPost) return
      // Sometimes these elements are lazily loaded and are not available during onMounted => try to fallback to the default elements just in case
      wishListElement = wishListEl ?? document.querySelector<HTMLElement>('#wish-list')
      timelineWishListEl = timelineWishListEl ?? document.querySelector<HTMLElement>('.wish-list-content')
      padletSectionsEl = padletSectionsEl ?? document.querySelector<HTMLElement>('padlet-sections')

      if (
        e.target === defaultWallContainerEl ||
        e.target === timelineWishListEl ||
        e.target === wishListElement ||
        e.target === wallContainerElement ||
        (e.target instanceof HTMLElement && e.target.classList.contains('wish-wrapper')) ||
        (e.target instanceof HTMLElement && e.target.classList.contains('timeline-section-posts-center-wrapper')) ||
        (e.target instanceof HTMLElement && e.target.classList.contains('timeline-sections-and-add-button-wrapper')) ||
        (e.target instanceof HTMLElement && e.target.classList.contains('wish-list-scrolling-wrapper')) ||
        (e.target instanceof HTMLElement && e.target.classList.contains('surface-section')) ||
        (e.target instanceof HTMLElement && e.target.id.startsWith('group-posts')) ||
        e.target === padletSectionsEl
      ) {
        e.preventDefault()
        const position = {
          left: 0,
          top: 0,
        }

        if (wishListElement != null) {
          const { left, top } = wishListElement?.getBoundingClientRect()
          const { scrollLeft, scrollTop } = wishListElement
          position.left = e.pageX - left + scrollLeft
          position.top = e.pageY - top + scrollTop
        }

        let sectionId: number | undefined
        if (surfaceStore.isShelf) {
          sectionId = checkIfClickAtEmptySpaceOfSection(e.clientX, e.clientY)
        }

        const attributes: { left?: number; top?: number; wall_section_id?: number } = { ...position }
        if (sectionId != null) {
          attributes.wall_section_id = sectionId
        }

        surfacePostsStore.startNewPost({ attributes })
      }
    }

    wallContainerElement?.addEventListener('dblclick', handleDoubleClick)
    if (wallContainerElement != null) {
      ;(wallContainerElement as HTMLElementWithMouseHandlers).$_onDblClick = handleDoubleClick
    }
  })

  onUnmounted(() => {
    const wallContainerElement = wallContainerEl ?? document.querySelector<HTMLElement>('#wall-container')
    const handleDoubleClick = (wallContainerElement as HTMLElementWithMouseHandlers)?.$_onDblClick
    if (handleDoubleClick != null) wallContainerEl?.removeEventListener('dblclick', handleDoubleClick)
  })
}
